/**
 * Audit log (GET /api/activity-logs, USERS bits) and player access log (GET /api/access-log,
 * PLAYERS bits). Traps: activity-logs ignores every filter and sort (always CreatedAt DESC);
 * access-log's allow-list names fields the table doesn't have (`IpAddress`, `action`), so neither
 * can be filtered until the backend work in roadmap 2B lands. Only paging is offered.
 */
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/api/http';
import { encodePagedQuery, pagedSchema, type PagedQuery, type SortDescriptor } from '@/api/paged-query';
import { jsonColumn } from '@/api/zod-helpers';
import { listSearchSchema, toPagedQuery } from '@/lib/url-state';

const optionalNumber = z.coerce
  .number()
  .int()
  .nullish()
  .transform((v) => v ?? null);
const optionalText = z
  .string()
  .nullish()
  .transform((v) => v ?? null);

// ---- audit log -----------------------------------------------------------------------------

export const activityLogSchema = z.object({
  Id: z.coerce.number().int(),
  BackofficeUsersId: optionalNumber,
  ActivityName: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  ActivityContext: jsonColumn(z.unknown()).optional(),
  CreatedAt: optionalText,
});

export type ActivityLog = z.infer<typeof activityLogSchema>;

export const auditSearchSchema = listSearchSchema({ sortFields: [], filters: {} });
export type AuditSearch = z.infer<typeof auditSearchSchema>;

/** Sorting and filters are ignored server-side, so the query carries paging only. */
export const auditPagedQuery = (search: AuditSearch): PagedQuery => ({ ...toPagedQuery(search), sort: null });

export const auditKeys = {
  all: ['activity-logs'] as const,
  list: (query: PagedQuery) => ['activity-logs', 'list', query] as const,
  detail: (id: number) => ['activity-logs', 'detail', id] as const,
};

export const auditListQuery = (search: AuditSearch) => {
  const query = auditPagedQuery(search);
  return queryOptions({
    queryKey: auditKeys.list(query),
    queryFn: ({ signal }) =>
      http('/activity-logs', { query: encodePagedQuery(query), signal }, pagedSchema(activityLogSchema)),
    placeholderData: keepPreviousData,
  });
};

export const auditEntryQuery = (id: number) =>
  queryOptions({
    queryKey: auditKeys.detail(id),
    queryFn: ({ signal }) => http(`/activity-logs/${id}`, { signal }, activityLogSchema),
  });

/** A one-line gist of an audit context: `operator #12`, `updated: Name, Status`, or its keys. */
export function summarizeContext(context: unknown): string {
  if (context === null || context === undefined) return '—';
  if (typeof context === 'string') return context.slice(0, 80);
  if (typeof context === 'number' || typeof context === 'boolean') return String(context);
  if (typeof context !== 'object') return '—';
  const c = context as Record<string, unknown>;
  const entity = typeof c.entity === 'string' ? c.entity : undefined;
  const id = typeof c.id === 'string' || typeof c.id === 'number' ? c.id : undefined;
  if (entity) return id !== undefined ? `${entity} #${id}` : entity;
  const keys = Object.keys(c);
  return keys.length === 0 ? '—' : keys.slice(0, 5).join(', ') + (keys.length > 5 ? ', …' : '');
}

// ---- access log ----------------------------------------------------------------------------

export const accessLogSchema = z.object({
  id: z.coerce.number().int(),
  playerId: optionalNumber,
  gameId: optionalNumber,
  /** Personal data: shown to holders of the player permissions only (the route's guard). */
  Ip: optionalText,
  CreatedAt: optionalText,
});

export type AccessLogEntry = z.infer<typeof accessLogSchema>;

export const ACCESS_SORT_FIELDS = ['CreatedAt'] as const;
export const DEFAULT_ACCESS_SORT: SortDescriptor = { field: 'CreatedAt', dir: 'desc' };

export const accessSearchSchema = listSearchSchema({ sortFields: ACCESS_SORT_FIELDS, filters: {} });
export type AccessSearch = z.infer<typeof accessSearchSchema>;

export const accessKeys = {
  list: (query: PagedQuery) => ['access-log', 'list', query] as const,
  detail: (id: number) => ['access-log', 'detail', id] as const,
};

export const accessListQuery = (search: AccessSearch) => {
  const query = toPagedQuery(search, { defaultSort: DEFAULT_ACCESS_SORT });
  return queryOptions({
    queryKey: accessKeys.list(query),
    queryFn: ({ signal }) =>
      http('/access-log', { query: encodePagedQuery(query), signal }, pagedSchema(accessLogSchema)),
    placeholderData: keepPreviousData,
  });
};

export const accessEntryQuery = (id: number) =>
  queryOptions({
    queryKey: accessKeys.detail(id),
    queryFn: ({ signal }) => http(`/access-log/${id}`, { signal }, accessLogSchema),
  });
